/**
 * 予測誤差評価 Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  startPredictionErrorEvaluation,
  getPredictionErrorEvaluationJob,
  listPredictionErrorEvaluationJobs,
  listAllPredictionErrorEvaluationJobs,
  deletePredictionErrorEvaluationJob,
} from '@/lib/api/ds/prediction-error-evaluation';
import { predictionErrorEvaluationKeys } from '../keys';
import type { StartPredictionErrorEvaluationRequest } from '@/lib/types/ds/prediction-error-evaluation';

/** 全アカウントのジョブ一覧を取得 */
export function useAllPredictionErrorEvaluationJobs(executedBy?: string) {
  const { getAuthHeader, isLoading: authLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: [...predictionErrorEvaluationKeys.allJobs(), executedBy ?? 'all'],
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return listAllPredictionErrorEvaluationJobs(authHeaders, 50, executedBy);
    },
    enabled: !authLoading && isAuthorized,
  });
}

/** ジョブ一覧を取得 */
export function usePredictionErrorEvaluationJobs(accountCode: string) {
  const { getAuthHeader, isLoading: authLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: predictionErrorEvaluationKeys.jobs(accountCode),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return listPredictionErrorEvaluationJobs(accountCode, authHeaders, 20);
    },
    enabled: !authLoading && isAuthorized && !!accountCode,
  });
}

/** ジョブ詳細を取得（running中は5秒ポーリング） */
export function usePredictionErrorEvaluationJob(accountCode: string, jobId?: string) {
  const { getAuthHeader, isLoading: authLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: predictionErrorEvaluationKeys.jobDetail(accountCode, jobId ?? ''),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getPredictionErrorEvaluationJob(accountCode, jobId!, authHeaders);
    },
    enabled: !authLoading && isAuthorized && !!accountCode && !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.job_status;
      if (status === 'pending' || status === 'running') return 5000;
      return false;
    },
  });
}

/** ジョブ起動Mutation（accountCodeをmutationFnの引数として受け取る） */
export function useStartPredictionErrorEvaluation() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async ({
      accountCode,
      request,
    }: {
      accountCode: string;
      request: StartPredictionErrorEvaluationRequest;
    }) => {
      const authHeaders = getAuthHeader();
      return startPredictionErrorEvaluation(accountCode, request, authHeaders);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: predictionErrorEvaluationKeys.jobs(variables.accountCode),
      });
      queryClient.invalidateQueries({
        queryKey: predictionErrorEvaluationKeys.allJobs(),
      });
    },
  });
}

/** ジョブ削除Mutation */
export function useDeletePredictionErrorEvaluation() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async ({ accountCode, jobId }: { accountCode: string; jobId: string }) => {
      const authHeaders = getAuthHeader();
      return deletePredictionErrorEvaluationJob(accountCode, jobId, authHeaders);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: predictionErrorEvaluationKeys.jobs(variables.accountCode),
      });
      queryClient.invalidateQueries({
        queryKey: predictionErrorEvaluationKeys.allJobs(),
      });
    },
  });
}
